import React from 'react';
import { Platform, Pressable, View, Text } from 'react-native';
import { Dialog, XStack, YStack } from 'tamagui';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface FeatureLimitModalProps {
  open: boolean;
  onClose: () => void;
  onUpgrade?: () => void;
  featureName?: string;
  currentUsage?: number;
  maxUsage?: number;
  upgradeBenefits?: string[];
}

const FeatureLimitModal: React.FC<FeatureLimitModalProps> = ({
  open,
  onClose,
  onUpgrade,
  featureName = 'knowledge articles',
  currentUsage = 10,
  maxUsage = 10,
  upgradeBenefits = [
    'Unlimited knowledge articles',
    'Advanced analytics',
    'Priority support',
    'Custom integrations',
  ]
}) => {
  const theme = themeSelector();
  const usagePercentage = (currentUsage / maxUsage) * 100;

  return (
    <Dialog key="featureLimit" open={open}>
      <Dialog.Portal>
        <Dialog.Overlay
          animation="quick"
          opacity={0.6}
          style={{
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(8px)',
            })
          }}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Dialog.Content
          bordered
          elevate
          animation={[
            'quick',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
          exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
          x={0}
          scale={1}
          style={[
            {
              padding: 0,
              minWidth: 500,
              maxWidth: 600,
              maxHeight: "85%",
              borderRadius: 24,
              overflow: 'hidden',
              elevation: 12,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.15,
              shadowRadius: 32,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
            },
            Platform.OS === 'web' && {
              backdropFilter: 'blur(24px)',
              boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
            }
          ]}
          opacity={1}
          y={0}
        >
          {/* Header */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack alignItems="center" gap="$3">
              <View style={{
                backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                borderRadius: 12,
                padding: 10,
              }}>
                <MaterialIcons
                  name="block"
                  size={20}
                  color={theme ? "#dc2626" : "#f87171"}
                />
              </View>
              <YStack flex={1}>
                <Dialog.Title style={{
                  fontSize: 20,
                  fontWeight: '700',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 4,
                  margin: 0,
                }}>
                  Feature Limit Reached
                </Dialog.Title>
                <Dialog.Description style={{
                  fontSize: 14,
                  color: theme ? '#64748b' : '#94a3b8',
                  fontWeight: '500',
                  margin: 0,
                }}>
                  You've reached your plan's limit
                </Dialog.Description>
              </YStack>
            </XStack>
          </View>

          {/* Content */}
          <View style={{ padding: 24 }}>
            <YStack gap="$4">
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}>
                <YStack gap="$3">
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 8,
                  }}>
                    You've used all available {featureName}
                  </Text>
                  <Text style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    lineHeight: 22,
                  }}>
                    Your current plan allows up to {maxUsage} {featureName}. Upgrade to create unlimited {featureName} and access more features.
                  </Text>
                </YStack>
              </View>

              {/* Usage Stats */}
              <View style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderRadius: 12,
                padding: 16,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}>
                <YStack gap="$2">
                  <XStack alignItems="center" justifyContent="space-between">
                    <Text style={{
                      fontSize: 14,
                      fontWeight: '600',
                      color: theme ? '#1e293b' : '#f1f5f9',
                    }}>
                      Current Usage
                    </Text>
                    <Text style={{
                      fontSize: 14,
                      fontWeight: '700',
                      color: theme ? '#dc2626' : '#f87171',
                    }}>
                      {currentUsage} / {maxUsage}
                    </Text>
                  </XStack>
                  <View style={{
                    height: 8,
                    backgroundColor: theme ? '#e5e7eb' : '#4b5563',
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}>
                    <View style={{
                      height: '100%',
                      width: `${usagePercentage}%`,
                      backgroundColor: theme ? '#dc2626' : '#f87171',
                      borderRadius: 4,
                    }} />
                  </View>
                </YStack>
              </View>

              {/* Upgrade Benefits */}
              <YStack gap="$2">
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? '#1e293b' : '#f1f5f9',
                  marginBottom: 8,
                }}>
                  Upgrade to get:
                </Text>
                {upgradeBenefits.map((benefit, index) => (
                  <XStack key={index} alignItems="center" gap="$2">
                    <MaterialIcons
                      name="check-circle"
                      size={20}
                      color={theme ? "#059669" : "#10b981"}
                    />
                    <Text style={{
                      fontSize: 14,
                      color: theme ? '#1e293b' : '#f1f5f9',
                    }}>
                      {benefit}
                    </Text>
                  </XStack>
                ))}
              </YStack>
            </YStack>
          </View>

          {/* Footer Actions */}
          <View style={{
            backgroundColor: theme ? '#f8fafc' : '#374151',
            paddingHorizontal: 24,
            paddingVertical: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#4b5563',
          }}>
            <XStack gap="$3" justifyContent="flex-end">
              <Dialog.Close asChild>
                <Pressable
                  onPress={onClose}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                    borderWidth: 1,
                    borderColor: theme ? '#e2e8f0' : '#6b7280',
                    minWidth: 100,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{
                    color: theme ? '#64748b' : '#d1d5db',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Cancel
                  </Text>
                </Pressable>
              </Dialog.Close>
              <Dialog.Close asChild>
                <Pressable
                  onPress={() => {
                    onClose();
                    onUpgrade?.();
                  }}
                  style={{
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    borderRadius: 12,
                    backgroundColor: theme ? '#059669' : '#10b981',
                    minWidth: 100,
                    alignItems: 'center',
                    elevation: 2,
                    shadowColor: '#059669',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.2,
                    shadowRadius: 8,
                  }}
                >
                  <Text style={{
                    color: '#ffffff',
                    fontWeight: '600',
                    fontSize: 14,
                  }}>
                    Upgrade Plan
                  </Text>
                </Pressable>
              </Dialog.Close>
            </XStack>
          </View>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

export default FeatureLimitModal;

